import React                 from "react";
import PropTypes             from "prop-types";
import {
    remove as _remove,
    isEmpty as _isEmpty,
    find as _find,
    cloneDeep as _cloneDeep,
} from "lodash";

import { AlertBox,
        RefluxComponent }   from "sp-react-commons";
import { ValidationSubmit, ValidationField } from "react-validation";
import MywortModalWrapper    from "../../../commons/components/MywortModalWrapper";
import Modal                 from "react-bootstrap/lib/Modal";
import Geolocation           from "react-geolocation";
import LocalityActions       from '../../actions/LocalityActions';
import PicturePreview        from "./../picture-upload-crop/PicturePreview";
import Translation           from "../../i18n/Translation";
import CommonsConstants      from "../../../commons/configurations/Constants";
import { GroupApi, PictureApi } from "../../api/Api";

const MAIN_PICTURE = {
    WIDTH: 1022,
    HEIGHT: 480,
    ROLE: "main",
    RATIO: 1022/480,
    PERCENT_RATIO: 480/1022 * 100
};


export default class GroupRequestModal extends RefluxComponent {

    static propTypes = {
        show                    : PropTypes.bool,
        onSubmit                : PropTypes.func,
        onHide                  : PropTypes.func,
        title                   : PropTypes.string,
        group                   : PropTypes.object
    };

    static defaultProps = {
        show                    : false,
        onSubmit                : () => {},
        onHide                  : () => {},
        title                   : Translation.translate("commons.create-group-request")
    };

    constructor(props) {
        super(props);

        this.state = this.initialState();
    }

    initialState = () => ({
        name: this.props.group ? this.props.group.name : "",
        comment: undefined,
        description: this.props.group ? this.props.group.description : "",
        geolocation: this.props.group ? this.props.group.geolocation : null,
        pictures: this.props.group ? this._initMainPictureCrop() : [],
        isValidCrop: true
    });

    _initMainPictureCrop = () => {
        let pictures = _cloneDeep(this.props.group.pictures);
        let mainPicture = this.getMainPicture(pictures);
        // Set cropCoordinates to default values if they are not set
        if(mainPicture && _isEmpty(mainPicture.cropCoordinates))
            mainPicture.cropCoordinates = [0, 0, mainPicture.width, parseInt(mainPicture.width / MAIN_PICTURE.RATIO)];

        return pictures;
    };

    onChangeName = (value) => this.setState({ name: value });
    onChangeComment = (value) => this.setState({ comment: value });
    onChangeDescription = (value) => this.setState({ description: value });
    onChangeLocality = (value) => this.setState({ geolocation: value });
    onSubmit = (errors) => {
        if(!errors) {
            this.props.onSubmit({
                name: this.state.name,
                geolocation: this.state.geolocation,
                comment: this.state.comment,
                description: this.state.description,
                pictures: this.state.pictures
            });
        }
    };

    onHide = () => {
        this.setState(this.initialState());
        this.props.onHide();
    };

    onUpdateMainPictureCrop = ({ picture, isValidCrop }) => {
        // Add/Update picture in picture list
        let pictures = _cloneDeep(this.state.pictures);
        let mainPicture = this.getMainPicture();
        if(mainPicture)
            _remove(pictures, (p) => p.id === mainPicture.id);
        pictures.push(picture);

        this.setState({pictures: pictures, isValidCrop: isValidCrop});
    };

    isCreationMode = () => _isEmpty(this.props.group);

    getMainPicture = (pictures = this.state.pictures) => this.getPictureByRole(pictures, MAIN_PICTURE.ROLE) || pictures[0]; // Fallback : take the first picture as main picture if there is no main picture
    getMainPictureUri = () => this.getMainPicture() ? this.getMainPicture().thumbnail : undefined;
    getMainPictureRawUri = () => this.props.group && this.getMainPicture() ? GroupApi.getGroupRawPictureUri(this.props.group.id) : undefined;
    getPictureByRole = (pictures, role) => _find(pictures, (picture) => picture && picture.role === role);

    searchLocality = (name, latitude, longitude, cb) => LocalityActions.getNearestLocalities(name, latitude, longitude, cb);

    renderComment = () => (
        <ValidationField name="comment" rules={{ required: false, whitespace: false }}>
            <textarea value={this.state.comment} onChange={this.onChangeComment} placeholder={Translation.translate("commons.comment-for-admin")} rows={4}/>
        </ValidationField>
    );

    renderPictures = () => (
        <div>
            <h4>{Translation.translate("commons.picture")}</h4>
            <PicturePreview
                picture={this.getMainPicture()}
                pictureUri={this.getMainPictureUri()}
                pictureRawUri={this.getMainPictureRawUri()}
                defaultPictureText={Translation.translate("picture-upload.drag-or-click-to-upload")}
                defaultPictureStyle={{ paddingBottom: MAIN_PICTURE.PERCENT_RATIO + "%" }}
                onUpdatePicture={this.onUpdateMainPictureCrop}
                uploadPicture={(pictureBlob) => PictureApi.uploadPicture(pictureBlob)}
                width={MAIN_PICTURE.WIDTH}
                height={MAIN_PICTURE.HEIGHT}
                role={MAIN_PICTURE.ROLE}
                cropAspectRatio={MAIN_PICTURE.RATIO}
                minCropWidth={CommonsConstants.MIN_CROP_WIDTH}
                maxFileSize={CommonsConstants.FILE_SIZE_MAX}
                errors={{
                    DEFAULT_ERROR             : Translation.translate("editor.group.picture-upload.errors.default-error"),
                    BAD_SIZE                  : Translation.translate("editor.group.picture-upload.errors.bad-size", { "maxSize" : CommonsConstants.FILE_SIZE_MAX / (1024 * 1024), "minWidth": CommonsConstants.MIN_CROP_WIDTH })
                }}
                translations={{
                    CROP_PICTURE              : Translation.translate("editor.group.picture-upload.crop-picture"),
                    PREVIEW_PICTURE           : Translation.translate("editor.group.picture-upload.preview-picture"),
                    UPLOAD_PICTURE            : Translation.translate("editor.group.picture-upload.upload-picture")
                }}
            />
        </div>
    );

    render = () => (
        <MywortModalWrapper show={this.props.show} onHide={this.onHide} backdrop="static">
            <Modal.Header className="mywort-modal__header" closeButton>
                <Modal.Title className="mywort-modal__title">{this.props.title}</Modal.Title>
            </Modal.Header>
            <AlertBox autoScroll
                      defaultMessage={Translation.translate("alert-box.default-message")}
                      reloadMessage={Translation.translate("alert-box.reload-message")}
                      class="mywort-alert-box mywort-alert-box--inline"
                      alertClasses={{
                          "DEFAULT": "mywort-alert-box--default",
                          "SUCCESS": "mywort-alert-box--success",
                          "INFO": "mywort-alert-box--info",
                          "WARNING": "mywort-alert-box--warning",
                          "ERROR": "mywort-alert-box--error"
                      }}
            />
            <Modal.Body>
                <ValidationField name="name" rules={{ required: true, max: CommonsConstants.GROUP_REQUEST_NAME_MAX_LENGTH }} showCharsLeft>
                    <input type="text" value={this.state.name} onChange={this.onChangeName} placeholder={Translation.translate("public.group-request.modal.group-name")}/>
                </ValidationField>
                <ValidationField name="description" rules={{ required: true, max: CommonsConstants.GROUP_REQUEST_DESCRIPTION_MAX_LENGTH }} showCharsLeft>
                    <textarea value={this.state.description} onChange={this.onChangeDescription} placeholder={Translation.translate("public.group-request.modal.description")} style={{ height: "94px" }}/>
                </ValidationField>
                <ValidationField name="geolocation" rules={{ type: "object", required: true}}>
                    <Geolocation value={this.state.geolocation}
                                 onChange={this.onChangeLocality}
                                 search={this.searchLocality}
                                 placeholder={Translation.translate("geolocation.placeholder")}
                                 chooseCityText={Translation.get("geolocation.choose-city")}
                                 fallbackCityText={Translation.get("geolocation.fallback-city")}
                                 language={Translation.getLanguageName()}
                    />
                </ValidationField>
                { this.isCreationMode() ? this.renderComment() : null }
                { this.renderPictures() }
                { this.props.children }
            </Modal.Body>
            <Modal.Footer className="mywort-modal__footer">
                <button className="mywort-btn mywort-btn--transparent-gray" onClick={this.onHide}>{Translation.translate("commons.cancel")}</button>
                <ValidationSubmit onSuccess={this.onSubmit} disabled={!this.state.isValidCrop} className="mywort-btn u-text-uppercase">{Translation.translate("commons.submit")}</ValidationSubmit>
            </Modal.Footer>
        </MywortModalWrapper>
    );

}



// WEBPACK FOOTER //
// ./js/mywort/commons/components/group-request/GroupRequestModal.jsx